/**
 * Cost Grid Component
 * Specialized grid layout for LLM Model cost fields
 * Displays 6 cost fields in a 2-column layout with proper formatting
 */

import React from 'react';
import './CostGrid.css';
import { LightbulbIcon } from '@bizfirst/common-react';

export interface CostData {
  textInputCost?: number;
  cachedTextInputCost?: number;
  audioInputCost?: number;
  cachedAudioInputCost?: number;
  textOutputCost?: number;
  audioOutputCost?: number;
}

export interface CostGridProps {
  costs: CostData;
  onChange: (field: string, value: number) => void;
  disabled?: boolean;
  currency?: string;
}

export const CostGrid: React.FC<CostGridProps> = ({
  costs,
  onChange,
  disabled = false,
  currency = 'USD',
}) => {
  const costFields = [
    {
      key: 'textInputCost',
      label: 'Text Input Cost',
      description: 'Cost per 1M input tokens (text)',
    },
    {
      key: 'cachedTextInputCost',
      label: 'Cached Text Input Cost',
      description: 'Cost per 1M cached input tokens (text)',
    },
    {
      key: 'audioInputCost',
      label: 'Audio Input Cost',
      description: 'Cost per 1M input tokens (audio)',
    },
    {
      key: 'cachedAudioInputCost',
      label: 'Cached Audio Input Cost',
      description: 'Cost per 1M cached input tokens (audio)',
    },
    {
      key: 'textOutputCost',
      label: 'Text Output Cost',
      description: 'Cost per 1M output tokens (text)',
    },
    {
      key: 'audioOutputCost',
      label: 'Audio Output Cost',
      description: 'Cost per 1M output tokens (audio)',
    },
  ];

  const handleChange = (field: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    onChange(field, numValue);
  };

  return (
    <div className="cost-grid-container">
      <div className="cost-grid-header">
        <h4 className="cost-grid-title">Pricing (per 1M tokens)</h4>
        <p className="cost-grid-currency">Currency: {currency}</p>
      </div>

      <div className="cost-grid-wrapper">
        {costFields.map((field) => (
          <div key={field.key} className="cost-grid-field">
            <label htmlFor={field.key} className="cost-field-label">
              {field.label}
            </label>
            <p className="cost-field-description">{field.description}</p>
            <div className="cost-field-input-wrapper">
              <span className="cost-currency-symbol">$</span>
              <input
                id={field.key}
                type="number"
                min="0"
                step="0.0001"
                value={costs[field.key as keyof CostData] ?? ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                disabled={disabled}
                placeholder="0.00"
                className="cost-field-input"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="cost-grid-info">
        <p className="cost-info-text">
          <LightbulbIcon size={12} /> Tip: Leave fields empty or set to 0 if not applicable for this model.
        </p>
      </div>
    </div>
  );
};

export default CostGrid;
